/**
 * Permission Hooks
 *
 * React hooks for checking permissions and managing drafts
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createClient } from '@utils/supabase/client';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@lib/database.types';
import {
  Permission,
  EntityType,
  DraftType,
  DraftStatus,
  type UserPermissions,
  type Draft,
} from '@lib/permissions';
import {
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  isAdmin,
  canUpdateDirectly,
  canDraftUpdate,
  canReviewDrafts,
  getUpdateStrategy,
  canCreate,
  canDelete,
  canViewAll,
  canViewOwn,
  canAccessDashboard,
  canCreateDraft,
  canCreatePublished,
  getDraftTableName,
  getEntityIdFieldName,
  getChangedFields,
  extractChangedData,
} from '@utils/permissions';

// ============================================================================
// FETCH USER PERMISSIONS
// ============================================================================

/**
 * Fetch user permissions from database (including role-based permissions)
 */
export async function getUserPermissions(
  supabase: SupabaseClient<Database>
): Promise<UserPermissions | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user?.id) return null;

  const user = session.user;

  // Get permissions from the database function
  const { data, error } = await supabase.rpc('get_user_permissions', {
    user_id: user.id,
  });

  if (error) {
    console.error('Error fetching permissions:', error);
    return null;
  }

  return {
    user_id: user.id,
    permissions: data?.map((row: any) => row.permission_name) || [],
  };
}

/**
 * Hook to get current user's permissions
 */
export function usePermissions() {
  const supabase = createClient();

  const query = useQuery({
    queryKey: ['userPermissions'],
    queryFn: () => getUserPermissions(supabase),
    staleTime: 30 * 1000, // Cache for 30 seconds (reduced from 5 minutes to minimize stale permission issues)
    retry: 3, // Retry failed requests to handle session refresh
    retryDelay: 1000, // Wait 1 second between retries to allow session refresh
  });

  const userPermissions = query.data;

  // Distinguish between "no permissions" and "not authenticated"
  // If query succeeded but returned null, user is not authenticated
  const isAuthenticated = query.isSuccess && userPermissions !== null;

  return {
    ...query,
    isAuthenticated,
    permissions: userPermissions?.permissions || [],
    // Utility functions
    hasPermission: (permission: Permission | string) =>
      hasPermission(userPermissions, permission),
    hasAnyPermission: (permissions: (Permission | string)[]) =>
      hasAnyPermission(userPermissions, permissions),
    hasAllPermissions: (permissions: (Permission | string)[]) =>
      hasAllPermissions(userPermissions, permissions),
    isAdmin: () => isAdmin(userPermissions),
    canUpdateDirectly: (
      entityType: EntityType,
      entityOwnerId?: string,
      currentUserId?: string
    ) => canUpdateDirectly(userPermissions, entityType, entityOwnerId, currentUserId),
    canDraftUpdate: (entityType: EntityType) => canDraftUpdate(userPermissions, entityType),
    canReviewDrafts: (entityType: EntityType) => canReviewDrafts(userPermissions, entityType),
    getUpdateStrategy: (
      entityType: EntityType,
      entityOwnerId?: string,
      currentUserId?: string
    ) => getUpdateStrategy(userPermissions, entityType, entityOwnerId, currentUserId),
    canCreate: (entityType: EntityType) => canCreate(userPermissions, entityType),
    canDelete: (entityType: EntityType, entityOwnerId?: string, currentUserId?: string) =>
      canDelete(userPermissions, entityType, entityOwnerId, currentUserId),
    canViewAll: (entityType: EntityType) => canViewAll(userPermissions, entityType),
    canViewOwn: (entityType: EntityType) => canViewOwn(userPermissions, entityType),
    canAccessDashboard: (section: 'users' | 'content' | 'teams' | 'sponsors') =>
      canAccessDashboard(userPermissions, section),
    canCreateDraft: () => canCreateDraft(userPermissions),
    canCreatePublished: () => canCreatePublished(userPermissions),
  };
}

/**
 * Hook to check a specific permission
 */
export function useHasPermission(permission: Permission | string): boolean {
  const { hasPermission } = usePermissions();
  return hasPermission(permission);
}

/**
 * Hook to check if user has any of the specified permissions
 */
export function useHasAnyPermission(permissions: (Permission | string)[]): boolean {
  const { hasAnyPermission } = usePermissions();
  return hasAnyPermission(permissions);
}

/**
 * Hook to check if user has all of the specified permissions
 */
export function useHasAllPermissions(permissions: (Permission | string)[]): boolean {
  const { hasAllPermissions } = usePermissions();
  return hasAllPermissions(permissions);
}

/**
 * Hook to check if user is an admin
 */
export function useIsAdmin(): boolean {
  const { isAdmin } = usePermissions();
  return isAdmin();
}

// ============================================================================
// DRAFT MANAGEMENT
// ============================================================================

/**
 * Fetch pending drafts for a specific entity type
 */
export async function getPendingDrafts<T = any>(
  supabase: SupabaseClient<Database>,
  entityType: EntityType,
  entityId?: number | string
): Promise<Draft<T>[]> {
  const tableName = getDraftTableName(entityType);
  const entityIdField = getEntityIdFieldName(entityType);

  let query = supabase
    .from(tableName as any)
    .select('*')
    .eq('status', DraftStatus.PENDING)
    .order('created_at', { ascending: false });

  if (entityId !== undefined) {
    query = query.eq(entityIdField, entityId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching drafts:', error);
    return [];
  }

  return data as unknown as Draft<T>[];
}

/**
 * Hook to get pending drafts
 */
export function usePendingDrafts<T = any>(entityType: EntityType, entityId?: number | string) {
  const supabase = createClient();

  return useQuery({
    queryKey: ['drafts', entityType, entityId],
    queryFn: () => getPendingDrafts<T>(supabase, entityType, entityId),
  });
}

/**
 * Create a draft update
 */
export async function createDraft<T = any>(
  supabase: SupabaseClient<Database>,
  entityType: EntityType,
  draftData: {
    entityId?: number | string;
    data: T;
    draftType: DraftType;
    changedFields?: string[];
  }
): Promise<Draft<T> | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user?.id) return null;
  const user = session.user;

  const tableName = getDraftTableName(entityType);
  const entityIdField = getEntityIdFieldName(entityType);

  const draftRecord = {
    [entityIdField]: draftData.entityId,
    draft_data: draftData.data,
    draft_type: draftData.draftType,
    created_by: user.id,
    changed_fields: draftData.changedFields,
  };

  const { data, error } = await supabase
    .from(tableName as any)
    .insert(draftRecord)
    .select()
    .single();

  if (error) {
    console.error('Error creating draft:', error);
    throw error;
  }

  return data as unknown as Draft<T>;
}

/**
 * Hook to create a draft
 */
export function useCreateDraft<T = any>(entityType: EntityType) {
  const supabase = createClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (draftData: {
      entityId?: number | string;
      data: T;
      draftType: DraftType;
      changedFields?: string[];
    }) => createDraft<T>(supabase, entityType, draftData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drafts', entityType] });
    },
  });
}

/**
 * Approve a draft
 */
/**
 * Approve a draft
 */
export async function approveDraft(
  supabase: SupabaseClient<Database>,
  entityType: EntityType,
  draftId: number,
  reviewNotes?: string
): Promise<boolean> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user?.id) return false;

  const tableName = getDraftTableName(entityType);

  // 1. Fetch the draft
  const { data: draft, error: fetchError } = await supabase
    .from(tableName as any)
    .select('*')
    .eq('id', draftId)
    .single();

  if (fetchError || !draft) {
    console.error('Error fetching draft:', fetchError);
    return false;
  }

  const draftObj = draft as any;
  const draftData = draftObj.draft_data;
  const entityId = draftObj[getEntityIdFieldName(entityType)];
  const type = draftObj.draft_type as DraftType;

  // 2. Apply changes based on entity type
  try {
    if (type === DraftType.DELETE) {
      // Handle Delete
      let table = '';
      switch (entityType) {
        case EntityType.TEAM: table = 'teams'; break;
        case EntityType.SPONSOR: table = 'sponsors'; break;
        case EntityType.CONTENT: table = 'news'; break;
        case EntityType.USER: table = 'profiles'; break; // Usually we don't delete profiles, but auth users
        // Add others as needed
      }

      if (table) {
        const { error } = await supabase.from(table as any).delete().eq('id', entityId);
        if (error) throw error;
      }
    } else {
      // Handle Create/Update
      if (entityType === EntityType.TEAM) {
        // 1. Upsert Team
        const teamData = {
          name: draftData.name,
          description: draftData.description,
          slug: draftData.slug,
          image: draftData.image,
          // Add other fields if necessary, but exclude relations
        };

        // If update, ensure ID is present
        if (type === DraftType.UPDATE && entityId) {
          (teamData as any).id = entityId;
        }

        const { data: team, error: teamError } = await supabase
          .from('teams')
          .upsert(teamData)
          .select('id')
          .single();

        if (teamError) throw teamError;
        const actualTeamId = team.id;

        // 2. Handle Trainers Relation
        if (draftData.trainers && Array.isArray(draftData.trainers)) {
          // Remove existing
          const { error: deleteError } = await supabase
            .from('teams_trainers')
            .delete()
            .eq('team_id', actualTeamId);

          if (deleteError) throw deleteError;

          // Add new
          if (draftData.trainers.length > 0) {
            const trainerInserts = draftData.trainers.map((t: any) => ({
              team_id: actualTeamId,
              trainer_id: t.profile_id,
            }));

            const { error: insertError } = await supabase
              .from('teams_trainers')
              .insert(trainerInserts);

            if (insertError) throw insertError;
          }
        }
      } else if (entityType === EntityType.SPONSOR) {
        const sponsorData = { ...draftData };
        if (type === DraftType.UPDATE && entityId) {
          sponsorData.id = entityId;
        }
        const { error } = await supabase.from('sponsors').upsert(sponsorData);
        if (error) throw error;

      } else if (entityType === EntityType.CONTENT) {
        const newsData = { ...draftData };
        if (type === DraftType.UPDATE && entityId) {
          newsData.id = entityId;
        }
        // Ensure status is PUBLISHED if it was a draft creation that is now approved
        if (type === DraftType.CREATE) {
          newsData.status = 'PUBLISHED';
        }

        // Handle hashtags if they are in draftData (assuming they might be)
        // For now, assuming newsData maps directly to columns or simple jsonb
        const { error } = await supabase.from('news').upsert(newsData);
        if (error) throw error;

      } else if (entityType === EntityType.USER) {
        // Profile updates
        const profileData = { ...draftData };
        // Remove trainer/board specific fields from profileData
        const trainerFields = ['trainer_qualification', 'trainer_additional_info', 'trainer_email', 'trainer_phone'];
        const boardFields = ['board_role', 'board_additional_info', 'board_email', 'board_phone', 'board_x_handle', 'board_instagram_handle', 'board_facebook_link'];

        trainerFields.forEach(f => delete profileData[f]);
        boardFields.forEach(f => delete profileData[f]);

        // Profile ID is user_id, usually not auto-increment
        const targetId = entityId || draftData.id;

        const { error } = await supabase
          .from('profiles')
          .update(profileData)
          .eq('user_id', targetId);

        if (error) throw error;

        // Handle Trainer Data
        if (draftData.trainer_email || draftData.trainer_phone || draftData.trainer_qualification) {
          const trainerData = {
            profile_id: targetId,
            qualification: draftData.trainer_qualification,
            additional_info: draftData.trainer_additional_info,
            email: draftData.trainer_email,
            phone: draftData.trainer_phone
          };
          const { error: trainerError } = await supabase.from('trainer').upsert(trainerData);
          if (trainerError) throw trainerError;
        }

        // Handle Board Data
        if (draftData.board_role || draftData.board_email) {
          const boardData = {
            profile_id: targetId,
            role: draftData.board_role,
            additional_info: draftData.board_additional_info,
            email: draftData.board_email,
            phone: draftData.board_phone,
            x_handle: draftData.board_x_handle,
            instagram_handle: draftData.board_instagram_handle,
            facebook_link: draftData.board_facebook_link
          };
          const { error: boardError } = await supabase.from('boardmembers').upsert(boardData);
          if (boardError) throw boardError;
        }

      } else {
        // Fallback for other types (Trainer Profile, Board Member)
        // Assuming they map directly to a table
        let table = '';
        let idField = 'id';

        if (entityType === EntityType.TRAINER) {
          table = 'trainer';
          idField = 'profile_id';
        } else if (entityType === EntityType.BOARD) {
          table = 'boardmembers';
          idField = 'profile_id';
        }

        if (table) {
          const updateData = { ...draftData };
          const targetId = entityId || draftData[idField];

          const { error } = await supabase
            .from(table as any)
            .upsert(updateData); // Trainer/Board tables use profile_id as PK

          if (error) throw error;
        }
      }
    }

    // 3. Delete the draft after successful approval
    // We delete instead of marking as approved to keep the draft tables clean
    // and prevent users from seeing stale approved drafts
    const { error: deleteError } = await supabase
      .from(tableName as any)
      .delete()
      .eq('id', draftId);

    if (deleteError) {
      console.error('Error deleting approved draft:', deleteError);
      // Even if delete fails, the changes were applied, so return true
      // The draft will be marked as orphaned but won't cause issues
    }

    return true;

  } catch (error) {
    console.error('Error applying draft changes:', error);
    return false;
  }
}

/**
 * Hook to approve a draft
 */
export function useApproveDraft(entityType: EntityType) {
  const supabase = createClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ draftId, reviewNotes }: { draftId: number; reviewNotes?: string }) =>
      approveDraft(supabase, entityType, draftId, reviewNotes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drafts', entityType] });
      // Also invalidate the entity data since it was updated
      queryClient.invalidateQueries({ queryKey: [entityType] });
    },
  });
}

/**
 * Reject a draft
 */
export async function rejectDraft(
  supabase: SupabaseClient<Database>,
  entityType: EntityType,
  draftId: number,
  reviewNotes?: string
): Promise<boolean> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user?.id) return false;
  const user = session.user;

  const tableName = getDraftTableName(entityType);

  const { error } = await supabase
    .from(tableName as any)
    .update({
      status: DraftStatus.REJECTED,
      reviewed_by: user.id,
      reviewed_at: new Date().toISOString(),
      review_notes: reviewNotes,
    })
    .eq('id', draftId);

  if (error) {
    console.error('Error rejecting draft:', error);
    return false;
  }

  return true;
}

/**
 * Hook to reject a draft
 */
export function useRejectDraft(entityType: EntityType) {
  const supabase = createClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ draftId, reviewNotes }: { draftId: number; reviewNotes?: string }) =>
      rejectDraft(supabase, entityType, draftId, reviewNotes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drafts', entityType] });
    },
  });
}

/**
 * Get draft count for current user
 */
export async function getDraftCount(
  supabase: SupabaseClient<Database>,
  entityType?: EntityType
): Promise<number> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user?.id) return 0;

  if (entityType) {
    const tableName = getDraftTableName(entityType);
    const { count, error } = await supabase
      .from(tableName as any)
      .select('*', { count: 'exact', head: true })
      .eq('status', DraftStatus.PENDING);

    if (error) {
      console.error('Error fetching draft count:', error);
      return 0;
    }

    return count || 0;
  }

  // Get total count across all entity types
  let totalCount = 0;
  for (const type of Object.values(EntityType)) {
    const tableName = getDraftTableName(type);
    const { count } = await supabase
      .from(tableName as any)
      .select('*', { count: 'exact', head: true })
      .eq('status', DraftStatus.PENDING);

    totalCount += count || 0;
  }

  return totalCount;
}

/**
 * Hook to get draft count
 */
export function useDraftCount(entityType?: EntityType) {
  const supabase = createClient();

  return useQuery({
    queryKey: ['draftCount', entityType],
    queryFn: () => getDraftCount(supabase, entityType),
  });
}

// ============================================================================
// UTILITY EXPORTS
// ============================================================================

export {
  getChangedFields,
  extractChangedData,
  getDraftTableName,
  getEntityIdFieldName,
};
